import api from './client';
import {post, get} from './http';
import {type ReviewSignals} from './moderationQueue';

export type Classification = {
  id: string;
  name: string;
  displayName: string;
  parentId: string | null;
  restricted: boolean;
  type: 'policy_violation' | 'vertical' | 'risk';
  category: 'Actor' | 'Content' | null;
  categories: Array<'Actor' | 'Content'>;
  hiddenFacets: Array<'Actor' | 'Content'>;
  viewContexts: string[];
  // Risk-only: flagged in admin as a Potential P0 risk category - triggers the mandatory,
  // fire-and-forget P0 escalation flow in ClassifyDialog instead of leaving P0 alerting as the
  // analyst's own opt-in choice.
  isPotentialP0: boolean;
  // Resolver-taxonomy risks this node maps to (from the taxonomy pack): the only Core Risks a
  // submission under it may carry. Optional so hand-built fixtures needn't spell it out.
  coreRisks?: string[];
  // The taxonomy concept this node stands for; what a moderation task's suggested_concept_ids match.
  conceptId?: string | null;
};

export type CreateClassificationRequest = {
  item_id: string;
  external_id?: string;
  url?: string;
  title?: string;
  thumbnail_url?: string;
  published_at?: string;
  author_id?: string;
  author_name?: string;
  author_handle?: string;
  // Only consumed when this classification creates the item - a `{Fields: [...]}` blob in the same
  // shape Scrapy produces. Ignored for an item that already exists, which already has its own.
  metadata?: Record<string, unknown>;
  affiliate: string;
  policy_violation: string;
  // Every risk picked under this policy violation, in selection order. One submission carries them
  // all: the dedup key is affiliate|policy_violation|submission_type|subject, so a second risk in
  // the same policy violation has nowhere else to go.
  risks: string[];
  platform: string;
  submission_type?: 'Actor' | 'Content';
  notes?: string;
  internal_notes?: string;
  // SHA-256 of screenshots uploaded via classificationsApi.uploadAttachment. Internal QA evidence:
  // never delivered to the client.
  attachment_sha256s?: string[];
  // Actor-only: example videos demonstrating the channel's violation. Free text, almost always
  // omitted.
  example_ids?: string;
  escalation_variant?: string;
  priority?: boolean;
  priority_subject?: string;
  // Hold delivery until the P0 raised from this classification is approved (removed if rejected).
  hold_for_p0?: boolean;
  // Set together when classifying a moderation task: this finishes the task atomically, or 409s if
  // the lease was lost (nothing written).
  moderation_task_id?: string;
  lease_id?: string;
  // Analytics only; sent with a moderation-task classification.
  review?: ReviewSignals;
  // Optional: why the engine's suggestion for the task was wrong.
  moderation_reason?: string;
  // Which classify flow this came from, for the submission history's "Via" badge. Omit for
  // moderation-queue submissions - moderation_task_id above already identifies those, and
  // takes priority server-side over whatever origin is sent alongside it.
  origin?: 'investigations' | 'single_submission' | 'image_search';
  enrichment?: {
    language?: string[];
    topic?: string[];
    location?: string[];
    core_risk?: string[];
  };
  // Explicit analyst opt-in to amend and resend an item already submitted under this exact
  // affiliate/policy_violation/submission_type/subject - without it the server 409s a duplicate
  // instead of minting the next revision. See SubmissionIdentity/ClassificationWriter.Resubmit.
  resubmit?: boolean;
};

export type ClassificationCreatedResponse = {
  classification_id: string;
  submission_content_id: string;
  /** The submission's stream id - what P0AlertsController.Create expects as submission_ids. */
  submission_stream_id: string;
  // The item the submission was recorded against - not necessarily the request's item_id, since an
  // item that already exists is resolved by identity. Flags live on items, so this is what the
  // dialog flags after submitting.
  item_id: string;
};

export const classificationsApi = {
  listConfig: async () => get<Classification[]>('/config/taxonomy'),

  create: async (body: CreateClassificationRequest) =>
    post<CreateClassificationRequest, ClassificationCreatedResponse>('/classifications', body),

  async uploadAttachment(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return post<FormData, ClassificationAttachment>('/classification-attachments', formData);
  },

  // Through the authenticated client, as an <img src> cannot carry the bearer token.
  async attachmentImage(sha256: string) {
    const {data} = await api.get<Blob>(`/classification-attachments/${sha256}`, {
      responseType: 'blob'
    });
    return data;
  },

  attachments: async (classificationId: string) =>
    get<{attachments: ClassificationAttachment[]}>(
      `/classifications/${classificationId}/attachments`
    )
};

export type ClassificationAttachment = {sha256: string; url: string};
